// Live Calls (T49, „Conversations > Live Calls“): wer telefoniert gerade, mit wem, seit wann. Rechts die Meetings von heute.
// Die Telefonie meldet Anrufe nur an den Anrufer; deshalb fragt `liveStore` alle 5 Sekunden nach. Ohne das Recht `gespraeche_alle`
// stehen hier nur die eigenen Gespräche.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { LiveGespraech, MeetingHeute } from "../api/gespraeche";
import { Avatar, Laden } from "../components/Bausteine";
import { FehlerBox } from "../components/Fehler";
import { Icon } from "../components/Icon";
import { Auswahl } from "../components/Auswahl";
import { useMeta } from "../daten/meta";
import { useSeitenKopf } from "../daten/seite";
import { T } from "../texte";
import { berlinTeile } from "../aktivitaeten/zeiten";
import { GespraecheKopf, useDarfAlle } from "./GespraecheKopf";
import { laufzeit } from "./logik";
import { useLive } from "./liveStore";
import "./gespraeche.css";

/** Sekundentakt für die Laufzeit, solange etwas angezeigt wird. */
function useSekunden(aktiv: boolean): number {
  const [jetzt, setJetzt] = useState(() => Date.now());
  useEffect(() => {
    if (!aktiv) return;
    setJetzt(Date.now());
    const t = window.setInterval(() => setJetzt(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [aktiv]);
  return jetzt;
}

function LiveKarte({ g, jetzt }: { g: LiveGespraech; jetzt: number }) {
  const klingelt = g.status === "ringing" || g.status === "initiated";
  const wer = g.kontakt_name ?? g.nummer_formatiert ?? g.nummer ?? "unbekannte Nummer";
  const satz = klingelt ? (g.richtung === "ausgehend" ? "ruft" : "wird angerufen von") : "spricht mit";
  return (
    <li className="gesp-live__karte">
      <span className={`gesp-live__symbol${klingelt ? " gesp-live__symbol--klingelt" : " gesp-live__symbol--aktiv"}`} aria-hidden="true">
        <Icon name="telefon" groesse={16} />
      </span>
      <div className="gesp-live__haupt">
        <div className="gesp-live__zeile">
          <Avatar name={g.user_name} id={g.user_id} groesse={18} />
          <span className="gesp-live__name">
            {g.user_name ?? "Unbekannt"} <span className="leise">{satz}</span> {wer}
            {klingelt && g.richtung === "ausgehend" ? <span className="leise"> an</span> : null}
          </span>
          <Icon name={g.richtung === "ausgehend" ? "ausgehend" : "eingehend"} groesse={13} className="leise" />
        </div>
        <div className="gesp-live__unter">
          {g.lead_id && g.lead_name ? (
            <Link to={`/lead/${encodeURIComponent(g.lead_id)}`} className="gesp-live__lead">
              {g.lead_name}
            </Link>
          ) : (
            <span>{g.lead_name ?? "Kein Lead zugeordnet"}</span>
          )}
          {g.nummer_formatiert && g.kontakt_name ? <span> · {g.nummer_formatiert}</span> : null}
        </div>
      </div>
      <div className="gesp-live__rechts">
        <span className="gesp-live__zeit" aria-label="Laufzeit">
          {klingelt ? "" : laufzeit(g.seit, jetzt)}
        </span>
        <span className={`gesp-marke ${klingelt ? "gesp-marke--gelb" : "gesp-marke--gruen"}`}>{g.status_text ?? (klingelt ? "Klingelt" : "Im Gespräch")}</span>
        {g.quelle === "close" ? (
          <span className="gesp-marke" title="Dieser Anruf läuft noch in Close; die Anzeige hängt bis zu einer Minute hinterher.">
            Close
          </span>
        ) : null}
      </div>
    </li>
  );
}

function Meetings({ meetings }: { meetings: MeetingHeute[] }) {
  return (
    <aside className="gesp-meetings" aria-label="Meetings heute">
      <h2 className="gesp-meetings__titel">Meetings heute</h2>
      {meetings.length ? (
        <ul>
          {meetings.map((m) => (
            <li key={m.id}>
              <span>
                {m.lead_id ? <Link to={`/lead/${encodeURIComponent(m.lead_id)}`}>{m.titel}</Link> : m.titel}
              </span>
              <span className="gesp-meetings__zeit">
                {m.beginn ? berlinTeile(m.beginn).zeit : ""}
                {m.ende ? ` – ${berlinTeile(m.ende).zeit}` : ""}
                {m.user_name ? ` · ${m.user_name}` : ""}
                {m.lead_name ? ` · ${m.lead_name}` : ""}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="gesp-meetings__leer">
          <span className="gesp-meetings__kreis" aria-hidden="true">
            <Icon name="kalender" groesse={15} />
          </span>
          Heute stehen keine Meetings an.
        </div>
      )}
    </aside>
  );
}

export function LiveGespraeche() {
  useSeitenKopf([{ label: T.nav.gespraeche, to: "/gespraeche" }, { label: "Live" }]);
  const meta = useMeta();
  const darfAlle = useDarfAlle();
  const live = useLive();
  const [user, setUser] = useState<string | null>(null);
  const jetzt = useSekunden(live.gespraeche.length > 0);

  const sichtbar = useMemo(() => {
    if (!user) return live.gespraeche;
    const n = meta.nutzer(user);
    const ids = new Set([user, n?.id, n?.close_user_id].filter(Boolean) as string[]);
    return live.gespraeche.filter((g) => g.user_id && ids.has(g.user_id));
  }, [live.gespraeche, user, meta]);

  return (
    <div className="gesp">
      <GespraecheKopf
        rechts={
          darfAlle ? (
            <span style={{ width: 170 }}>
              <Auswahl
                optionen={meta.nutzerListe.map((n) => ({ wert: n.close_user_id ?? n.id, label: n.name, unter: n.is_active ? null : "inaktiv" }))}
                wert={user}
                leerErlaubt
                platzhalter="Alle Mitarbeiter"
                breite={240}
                titel="Nach Mitarbeiter filtern"
                onWahl={(w) => setUser((w as string | null) || null)}
              />
            </span>
          ) : undefined
        }
      />
      <div className="gesp-live">
        <section aria-live="polite">
          <h2 className="gesp-live__titel">
            Laufende Gespräche
            {sichtbar.length ? <span className="gesp-zaehler">{sichtbar.length}</span> : null}
          </h2>
          {live.fehler && !live.gespraeche.length ? <FehlerBox fehler={live.fehler} onNochmal={live.neuLaden} /> : null}
          {!live.geladen && !live.fehler ? <Laden klein /> : null}
          {live.geladen && !sichtbar.length && !(live.fehler && !live.gespraeche.length) ? (
            <div className="gesp-leerkarte">
              <span className="gesp-leerkarte__kreis" aria-hidden="true">
                <Icon name="telefon" groesse={20} />
              </span>
              <strong>Gerade läuft kein Gespräch.</strong>
              <span>
                {darfAlle ? "Sobald jemand telefoniert, erscheint das Gespräch hier." : "Hier erscheinen deine Gespräche, solange du telefonierst."}
              </span>
            </div>
          ) : null}
          {sichtbar.length ? (
            <ul className="gesp-live__liste">
              {sichtbar.map((g) => (
                <LiveKarte key={g.crm_call_id ?? g.call_id} g={g} jetzt={jetzt} />
              ))}
            </ul>
          ) : null}
          {live.fehler && live.gespraeche.length ? <p className="gesp-hinweis">Der Stand konnte zuletzt nicht aktualisiert werden. Die Anzeige versucht es weiter.</p> : null}
        </section>
        <Meetings meetings={live.meetings} />
      </div>
    </div>
  );
}
